import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CircleCheck, CircleX, Clock, Landmark, Loader2, Send } from "lucide-react";

import ContractPreviewModal from "@/components/application/merchant-tasks/send/ContractPreviewModal";
import SendWarnings from "@/components/application/merchant-tasks/send/SendWarnings";
import { useContractPreview } from "@/components/application/merchant-tasks/send/useContractPreview";
import { FlowStepper } from "@/components/application/merchant-tasks/primitives";
import type { SendRequest } from "@/components/application/merchant-tasks/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { formatDate, formatDateTime } from "@/utils/dateUtils";

import { agentClient } from "../../agentClient";
import { THREAD_CARD } from "../../choices/cardWidth";
import type { WireChoice } from "../../protocol/protocol";
import type { AgentState } from "../../store/agentSlice";
import { selectChoice } from "../../store/selectors";
import {
  AssistantCard,
  AssistantCardBody,
  AssistantCardFooter,
  AssistantCardHeader,
} from "../../theme";
import { useOnFake } from "../useOnFake";
import { FlowAnalysisHeader, useFlowAnalysis } from "./FlowAnalysis";
import { FlowDocumentsBody, MessageEditor } from "./FlowDocumentsBody";
import { FlowSettings } from "./FlowSettings";
import { FlowTerms } from "./FlowTerms";
import {
  changesContract,
  flowCard,
  flowOverrides,
  pastTense,
  presentTense,
  proposedSteps,
  type FlowCardPayload,
  type FlowEdits,
} from "./payload";

const APPROVE = "approve";
/** How long a pointer down stands for the click that follows it. */
const PRESS_MEMORY_MS = 1_000;
const DENY = "deny";
const VIA_LABELS: Record<string, string> = {
  web: "web",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
};

/** The card's place in its life, from its choice. */
export type FlowCardState =
  | "pending"
  | "sending"
  | "sent"
  | "failed"
  | "declined"
  | "superseded"
  | "closed"
  | "expired";

export function flowCardState(choice: WireChoice): FlowCardState {
  if (choice.status === "pending") return "pending";
  if (choice.status === "expired") return "expired";
  // Only a newer card supersedes; new input, a stop or the run's end closes it.
  if (choice.status === "cancelled") {
    return choice.cancel_reason === "superseded" ? "superseded" : "closed";
  }
  if (choice.chosen !== APPROVE) return "declined";
  if (choice.error) return "failed";
  return choice.executed ? "sent" : "sending";
}

const HEADER: Record<FlowCardState, { icon: typeof Send; tone: "primary" | "success" | "muted" }> =
  {
    pending: { icon: Send, tone: "primary" },
    sending: { icon: Send, tone: "primary" },
    sent: { icon: CircleCheck, tone: "success" },
    failed: { icon: CircleX, tone: "muted" },
    declined: { icon: CircleX, tone: "muted" },
    superseded: { icon: Clock, tone: "muted" },
    closed: { icon: Clock, tone: "muted" },
    expired: { icon: Clock, tone: "muted" },
  };

/** The last press's refusal, and the card it was made on (a redraw makes it the new card's note). */
interface Notice {
  text: string;
  shown: string | null | undefined;
}

function errorText(reason: unknown, fallback: string): string {
  const message = (reason as { message?: unknown } | null)?.message;
  return typeof message === "string" && message ? message : fallback;
}

/** A figure in Quick Look's style: the value over a small uppercase label. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="truncate text-xs font-semibold tabular-nums text-foreground">{value || "—"}</p>
      <p className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
    </div>
  );
}

function monthYear(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
}

function TemplateNames({ card, pending }: { card: FlowCardPayload; pending: boolean }) {
  const rows = card.template ?? [];
  if (!rows.length) return null;
  return (
    <div className={cn("space-y-1", pending && "opacity-50")}>
      {rows.map((row) => (
        <p key={row.key} className="text-[12.5px] font-semibold text-foreground">
          {row.name}
        </p>
      ))}
    </div>
  );
}

/** The open flows Send cancels: one line each, so nothing a merchant is in goes unseen. */
function Replaces({ card }: { card: FlowCardPayload }) {
  if (!card.replaces?.length) return null;
  return (
    <ul aria-label="Cancels" className="space-y-1 border-t pt-2">
      {card.replaces.map((flow) => (
        <li key={flow.merchant_task_id} className="text-xs text-muted-foreground">
          Cancels <b className="font-medium text-foreground">{flow.label}</b> sent{" "}
          {formatDate(flow.created_at)}
          {flow.current_step ? ` · on ${flow.current_step}` : ""}
        </li>
      ))}
    </ul>
  );
}

function BankBody({ card }: { card: FlowCardPayload }) {
  const window = card.statement_window;
  const addendum = (card.template ?? [])[0];
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3">
        {window ? (
          <Fact
            label="Statements"
            value={`${monthYear(window.start)} – ${monthYear(window.end)}`}
          />
        ) : null}
        {addendum ? <Fact label={addendum.label} value={addendum.name} /> : null}
      </div>
      {card.banks?.length ? (
        <section className="space-y-1.5 border-t pt-2">
          <h4 className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
            Connected banks
          </h4>
          <ul className="space-y-1.5">
            {card.banks.map((bank) => (
              <li key={`${bank.institution}-${bank.connected_at}`} className="flex gap-2 text-xs">
                <Landmark className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 font-semibold">
                    {bank.institution || "Bank"}
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-px text-[10px] font-medium",
                        bank.status === "active"
                          ? "bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-200"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {bank.status === "active"
                        ? "Active"
                        : bank.status === "revoked"
                          ? "Revoked"
                          : "Login required"}
                    </span>
                  </p>
                  <p className="text-muted-foreground">
                    {[
                      ...bank.accounts.map((mask) => `••${mask}`),
                      `Connected ${formatDate(bank.connected_at)}`,
                    ].join(" · ")}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

interface FooterProps {
  card: FlowCardPayload;
  choice: WireChoice;
  state: FlowCardState;
  previewing: boolean;
  onPreviewing: (checked: boolean) => void;
  busy: boolean;
  /** "Update" while edits that change the contract wait for the redraw; else the approve label. */
  primaryLabel: string;
  notice: Notice | null;
  onApprove: () => void;
  /** The primary press begins (pointer down): what it says then is what the click does. */
  onPrimaryDown: () => void;
  /** That pointer press ended without a click, or a key activates the button instead. */
  onPrimaryLost: () => void;
  onDeny: () => void;
}

function Receipt({
  choice,
  card,
  state,
}: {
  choice: WireChoice;
  card: FlowCardPayload;
  state: FlowCardState;
}) {
  const via = choice.decided_via && VIA_LABELS[choice.decided_via];
  const when = choice.decided_at ? formatDateTime(choice.decided_at) : null;
  const done = pastTense(card.labels.approve);
  const lead: Record<FlowCardState, string> = {
    pending: "",
    sending: `${presentTense(card.labels.approve)}…`,
    sent: via ? `${done} on ${via}` : done,
    failed: choice.activity?.failed ?? `Not ${done.toLowerCase()}`,
    declined: card.labels.declined ?? "Declined · nothing sent",
    superseded: "Superseded · nothing sent",
    closed: "Closed · nothing sent",
    expired: "Expired · nothing sent",
  };
  const Icon =
    state === "sent"
      ? CircleCheck
      : state === "sending"
        ? Loader2
        : state === "failed" || state === "declined"
          ? CircleX
          : Clock;
  return (
    <div
      role="status"
      data-state={state}
      className={cn(
        "flex w-full items-start gap-2 text-xs",
        state === "sent"
          ? "text-success"
          : state === "failed"
            ? "text-destructive"
            : "text-muted-foreground"
      )}
    >
      <Icon
        className={cn("mt-px size-3.5 shrink-0", state === "sending" && "animate-spin")}
        aria-hidden
      />
      <span className="min-w-0 [overflow-wrap:anywhere]">
        {[
          lead[state],
          state === "sent" || state === "declined" ? when : null,
          state === "failed" ? choice.error : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </span>
    </div>
  );
}

function Footer({
  card,
  choice,
  state,
  previewing,
  onPreviewing,
  busy,
  primaryLabel,
  notice,
  onApprove,
  onPrimaryDown,
  onPrimaryLost,
  onDeny,
}: FooterProps) {
  if (state !== "pending") return <Receipt choice={choice} card={card} state={state} />;
  const preview = card.preview && card.preview.offered ? card.preview : null;
  const locked = Boolean(preview?.required);
  const recipient = (
    <p className="min-w-0 text-xs text-muted-foreground [overflow-wrap:anywhere]">
      To <b className="font-medium text-foreground">{card.recipient.email}</b>
    </p>
  );
  const buttons = (
    <div className="flex shrink-0 gap-2 max-sm:w-full [&_button]:max-sm:h-[38px] [&_button]:max-sm:flex-1 [&_button]:max-sm:text-sm">
      <Button
        type="button"
        size="sm"
        disabled={busy || !card.sendable}
        onPointerDown={onPrimaryDown}
        // Not pointerleave: on touch it fires between pointerup and the click.
        onPointerCancel={onPrimaryLost}
        onKeyDown={onPrimaryLost}
        onClick={onApprove}
      >
        {busy ? <Loader2 className="animate-spin" /> : null}
        {primaryLabel}
      </Button>
      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onDeny}>
        {card.labels.deny}
      </Button>
    </div>
  );
  return (
    <div className="flex w-full flex-col gap-2">
      {preview ? (
        <>
          {recipient}
          <div className="flex flex-wrap items-center justify-between gap-2 max-sm:flex-col max-sm:items-stretch">
            <label
              className={cn(
                "flex items-center gap-2 text-xs max-sm:text-[13px]",
                locked && "text-muted-foreground"
              )}
            >
              <Checkbox
                checked={locked || previewing}
                disabled={locked || busy}
                className={cn(locked && "opacity-55")}
                onCheckedChange={(checked) => onPreviewing(checked === true)}
              />
              {preview.label}
            </label>
            {buttons}
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 max-sm:flex-col max-sm:items-stretch">
          {recipient}
          {buttons}
        </div>
      )}
      {!card.sendable && card.blocked_reason ? (
        <p className="text-xs text-destructive">{card.blocked_reason}</p>
      ) : null}
      {notice ? (
        // A refusal on this card is an error; once the card redrew (the Review it asked for),
        // it is the note that says why.
        notice.shown === choice.args_sha256 ? (
          <p role="alert" className="text-xs text-destructive">
            {notice.text}
          </p>
        ) : (
          <p role="status" className="text-xs text-muted-foreground">
            {notice.text}
          </p>
        )
      ) : null}
    </div>
  );
}

/**
 * `send_merchant_flow`'s approval as the web's flow card (F2): the merchant's stepper beside the
 * flow's body (the contract's analysis, template and offer; a bank connection's window and banks;
 * a request's message and documents), the warnings and the changed settings, then the recipient,
 * "Preview contract" and Send. Its changes go back as the press's overrides, named by the card they
 * were made on (`shown`). With the preview checked, Send mints the preview from `card.request` as
 * it is, and the preview's Confirm is the press. After the press it is the receipt.
 */
export function FlowApprovalCard({ choiceRef }: { choiceRef: string }) {
  const choice = useSelector((s: { agent: AgentState }) => selectChoice(s, choiceRef));
  // Above the view's key, so a Review's sentence is still there on the card it redrew, and an
  // unchecked "Preview contract" stays unchecked.
  const [notice, setNotice] = useState<Notice | null>(null);
  const [previewing, setPreviewing] = useState(true);
  const card = flowCard(choice);
  if (!choice || !card) return null;
  // A redrawn card (its stored call changed) starts from what it now shows.
  return (
    <FlowCardView
      key={choice.args_sha256 ?? choice.ref}
      choice={choice}
      card={card}
      notice={notice}
      onNotice={setNotice}
      previewing={previewing}
      onPreviewing={setPreviewing}
    />
  );
}

interface FlowCardViewProps {
  choice: WireChoice;
  card: FlowCardPayload;
  notice: Notice | null;
  onNotice: (notice: Notice | null) => void;
  previewing: boolean;
  onPreviewing: (previewing: boolean) => void;
}

function FlowCardView({
  choice,
  card,
  notice,
  onNotice,
  previewing,
  onPreviewing,
}: FlowCardViewProps) {
  const state = flowCardState(choice);
  const readOnly = state !== "pending";
  const [edits, setEdits] = useState<FlowEdits>({});
  const [busy, setBusy] = useState(false);
  // What the primary button said when the press began (pointer down), before a blur settles.
  // A pointer down that never became a click (dragged off, cancelled) is forgotten, and an old
  // one is ignored, so a later keyboard press is never read as it.
  const pressedAs = useRef<{ mode: "update" | "send"; at: number } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const preview = useContractPreview(card.application.id);
  const isContract = card.body === "contract";
  // Today's analysis is the proposal's context; a settled card shows what was decided only.
  const analysis = useFlowAnalysis(
    card.application.id,
    !readOnly && isContract && card.terms != null
  );

  const overrides = flowOverrides(card, edits);
  // Edits to the contract come back on a redrawn card (its template, steps and figures as the
  // server computes them); only a press with none is the send.
  const contractPending = changesContract(overrides);
  // On the picked fake Send presses at once: a preview would mint a DocuSeal submission.
  const onFake = useOnFake(card, choice.thread_id);
  const wantsPreview =
    !onFake && Boolean(card.preview?.offered && (card.preview.required || previewing));
  const press = async (option: string, extra?: Record<string, unknown>) => {
    const sent =
      option === APPROVE ? { ...flowOverrides(card, edits, { settle: true }), ...extra } : {};
    if (Object.keys(sent).length && !choice.args_sha256) {
      // Changes name the card they were made on; without its hash they cannot go back.
      throw new Error("This card can't take changes; ask the assistant to propose it again.");
    }
    // An approve names the card it was pressed on, changes or not: a tab still showing a card
    // since redrawn never approves the new one ("This card changed; review it.").
    const changes =
      option === APPROVE && choice.args_sha256
        ? {
            shown: choice.args_sha256,
            ...(Object.keys(sent).length > 0 && { overrides: sent }),
          }
        : undefined;
    await agentClient.decide(choice.ref, option, undefined, undefined, changes);
  };

  const run = async (work: () => Promise<void>, fallback: string) => {
    setBusy(true);
    onNotice(null);
    try {
      await work();
    } catch (reason) {
      onNotice({ text: errorText(reason, fallback), shown: choice.args_sha256 });
    } finally {
      setBusy(false);
    }
  };

  const onApprove = () => {
    const recent =
      pressedAs.current && Date.now() - pressedAs.current.at < PRESS_MEMORY_MS
        ? pressedAs.current.mode
        : null;
    const began = recent ?? (contractPending ? "update" : "send");
    pressedAs.current = null;
    // The fee as the press sends it (settled to the server's step, as a blur does).
    const updating = changesContract(flowOverrides(card, edits, { settle: true }));
    // A press that began as Update never sends: if the box's blur settled the change away
    // before the click landed, the card now reads Send and the click does nothing.
    if (began === "update" && !updating) return;
    return run(async () => {
      // "Update": the press redraws the card with the changes; the server never sends them.
      if (updating) return press(APPROVE);
      if (!wantsPreview) return press(APPROVE);
      await preview.start(card.request as unknown as SendRequest);
      setModalOpen(true);
    }, "This couldn't be sent.");
  };

  const onConfirm = () =>
    void preview
      .confirm(async (request) => {
        // A PDF preview mints nothing: the press sends the request it showed, with no preview.
        const id = request.preview_submission_id;
        await press(APPROVE, id != null ? { preview_submission_id: id } : undefined);
      })
      .then(() => {
        preview.release();
        setModalOpen(false);
      })
      .catch((reason: unknown) => preview.showError(errorText(reason, "This couldn't be sent.")));

  const onCancelPreview = () => {
    setModalOpen(false);
    void preview.discard();
  };

  const settingsChanged = (key: string, value: unknown) =>
    setEdits((prev) => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
  const { icon, tone } = HEADER[state];
  const muted =
    state === "declined" || state === "superseded" || state === "closed" || state === "expired";

  return (
    <AssistantCard
      data-flow-card={card.flow_type}
      data-state={state}
      className={cn(
        THREAD_CARD,
        "max-w-[30rem]",
        (state === "superseded" || state === "closed" || state === "expired") && "border-dashed"
      )}
    >
      <AssistantCardHeader icon={icon} title={card.title} tone={tone} />
      <AssistantCardBody
        className={cn(
          "grid grid-cols-[164px_minmax(0,1fr)] gap-0 border-y p-0 max-sm:grid-cols-1",
          muted && "opacity-60"
        )}
      >
        <div
          className={cn(
            "border-r bg-muted/50 p-2 max-sm:border-b max-sm:border-r-0 max-sm:px-3",
            contractPending && "opacity-50"
          )}
        >
          <FlowStepper steps={proposedSteps(card)} currentKey={null} />
        </div>
        <div className="flex min-w-0 flex-col gap-2.5 px-3 py-2.5 text-xs">
          {isContract && card.terms ? <FlowAnalysisHeader analysis={analysis} /> : null}
          {card.body === "contract" || card.body === "other" ? (
            <TemplateNames card={card} pending={contractPending} />
          ) : null}
          {isContract && card.terms ? (
            <FlowTerms
              terms={card.terms}
              fee={edits.fee}
              readOnly={readOnly}
              pending={contractPending}
              onFee={(fee) => setEdits((prev) => ({ ...prev, fee }))}
            />
          ) : null}
          {card.body === "bank" ? <BankBody card={card} /> : null}
          {card.body === "documents" ? (
            <FlowDocumentsBody
              card={card}
              edits={edits}
              readOnly={readOnly}
              onEdits={(next) => setEdits((prev) => ({ ...prev, ...next }))}
            />
          ) : card.message.editable && card.overrides.includes("message") && !readOnly ? (
            <MessageEditor
              message={card.message}
              value={edits.message}
              readOnly={readOnly}
              onChange={(message) => setEdits((prev) => ({ ...prev, message }))}
            />
          ) : null}
          <SendWarnings warnings={card.warnings} size="xs" />
          <Replaces card={card} />
          {card.overrides.includes("settings") ? (
            <FlowSettings
              settings={card.settings}
              values={edits.settings ?? {}}
              readOnly={readOnly}
              onChange={settingsChanged}
            />
          ) : null}
        </div>
      </AssistantCardBody>
      <AssistantCardFooter className="pt-2">
        <Footer
          card={card}
          choice={choice}
          state={state}
          previewing={previewing}
          onPreviewing={onPreviewing}
          busy={busy || preview.busy}
          primaryLabel={contractPending ? "Update" : card.labels.approve}
          notice={notice}
          onApprove={onApprove}
          onPrimaryLost={() => {
            pressedAs.current = null;
          }}
          onPrimaryDown={() => {
            pressedAs.current = { mode: contractPending ? "update" : "send", at: Date.now() };
          }}
          onDeny={() => run(() => press(DENY), "This couldn't be recorded.")}
        />
      </AssistantCardFooter>
      <ContractPreviewModal
        open={modalOpen}
        preview={preview.preview}
        confirming={preview.busy}
        completed={preview.completed}
        error={preview.actionError}
        isDocument={card.flow_type === "document"}
        onConfirm={onConfirm}
        onCancel={onCancelPreview}
        onCompleted={preview.markCompleted}
        onRefresh={() => void preview.refresh()}
        onEditAgain={() => void preview.editAgain()}
      />
    </AssistantCard>
  );
}
